import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { billAccountReloadMinimumDue } from '@amfam/billing/billaccount/data-access/lib/+state/billaccount.actions';
import { editPaymentMethodSuccess } from '@amfam/billing/payment-method/data-access/lib/+state/payment-method/payment-method.actions';
import { selectPaymentMethodEntities } from '@amfam/billing/payment-method/data-access/lib/+state/payment-method/payment-method.selectors';
import {
  PaymentAccountActionTypes,
  PaymentAccountsLoad,
  PaymentAccountsLoadDetail
} from '@amfam/billing/paymentaccount/data-access/lib/+state/paymentaccount.actions';
import { selectPaymentAccountEntities } from '@amfam/billing/paymentaccount/data-access/lib/+state/paymentaccount.selectors';
import { PaymentService } from '@amfam/billing/shared/util/lib/services/payment.service';
import type { ApiStatus } from '@amfam/shared/models/lib/api/api-status.model';
import {
  BillingPaymentPaths,
  INVALID_IMPERSONATION_STATUS_CODE
} from '@amfam/shared/models/lib/billing/billing-constants';
import type { GetRegisteredPaymentsResponse } from '@amfam/shared/models/lib/billing/payment-history.models';
import {
  UNAVAILABLE_PAYMENT_ID,
  type ScheduledPayment,
  type SchedulePaymentPayloadModel,
  type SubmitScheduledPaymentPayloadModel
} from '@amfam/shared/models/lib/billing/scheduled-payment.model';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';

import {
  ScheduledPaymentsActions,
  ScheduledPaymentsActionTypes,
  scheduledPaymentsCleanup,
  scheduledPaymentsDelete,
  scheduledPaymentsDeleteFail,
  scheduledPaymentsDeleteSuccess,
  scheduledPaymentsEdit,
  scheduledPaymentsEditFail,
  scheduledPaymentsEditSuccess,
  scheduledPaymentsLoad,
  scheduledPaymentsLoadFail,
  scheduledPaymentsLoadSuccess,
  scheduledPaymentsSubmit,
  scheduledPaymentsSubmitFail,
  scheduledPaymentsSubmitSuccess,
  scheduledPaymentsTruncate,
  scheduledPaymentsUpdate
} from './schedulepayment.actions';

export const loadEffect = createEffect(
  (action$ = inject(Actions), paymentService = inject(PaymentService)) => {
    return action$.pipe(
      ofType(ScheduledPaymentsActionTypes.ScheduledPaymentsLoadType),
      switchMap(() => {
        const startDate = DateTime.now().minus({ days: 31 }).toFormat('yyyy-MM-dd');
        const endDate = DateTime.now().plus({ days: 31 }).toFormat('yyyy-MM-dd');
        return paymentService.getRegisteredPayments(startDate, endDate, false, true).pipe(
          map((response: GetRegisteredPaymentsResponse) => response.payments ?? []),
          map((payments: ScheduledPayment[]) =>
            // Normalize the billAccount number to be a string for future comparisons and consistancy across API calls

            payments.map(payment => {
              payment.billAccounts = payment.billAccounts.map(billAccount => {
                billAccount.billAccountNumber = String(billAccount.billAccountNumber);
                return billAccount;
              });
              return payment;
            })
          ),
          map((payments: ScheduledPayment[]) => scheduledPaymentsLoadSuccess(payments)),
          catchError(error => of(scheduledPaymentsLoadFail(error)))
        );
      })
    );
  },
  { functional: true }
);

export const addEffect = createEffect(
  (action$ = inject(Actions), paymentService = inject(PaymentService)) => {
    return action$.pipe(
      ofType(scheduledPaymentsSubmit),
      map(action => action.payload),
      switchMap((payload: SubmitScheduledPaymentPayloadModel) =>
        paymentService.addScheduledPayment(payload).pipe(
          mergeMap(res => {
            const returnArray: Array<ScheduledPaymentsActions> = [];
            /**
             * Status code of '202304' will be returned if the user is impersonating and is
             * trying to schedule a payment without sufficient permission to do so.
             * In this case treat the response code of '202304' as a failure code and stop them from
             * moving forward.
             */
            if (res.status?.messages?.[0]?.code === INVALID_IMPERSONATION_STATUS_CODE) {
              returnArray.push(
                scheduledPaymentsSubmitFail({
                  request: payload,
                  status: res.status
                })
              );
            } else {
              const conf = paymentService.buildAddScheduledPaymentConfirmationData(payload, res);
              // TODO - instead of only passing the requestJSON from the payload, pass the whole thing.
              returnArray.push(
                scheduledPaymentsSubmitSuccess({
                  request: {
                    ...payload.requestJson,
                    paymentMethodType: payload.paymentMethodType,
                    correlationId: payload.correlationId
                  },
                  response: res,
                  confirmation: conf
                }),
                scheduledPaymentsTruncate(UNAVAILABLE_PAYMENT_ID)
              );
            }
            return returnArray;
          }),
          catchError(error =>
            of(
              scheduledPaymentsSubmitFail({
                request: payload,
                status: error
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

// When adding a scheduled payment fails we need to cleanup the state (error state is alread in the store)
export const addFailCleanupEffect = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(ScheduledPaymentsActionTypes.ScheduledPaymentsSubmitFailType),
      map(() => scheduledPaymentsTruncate(UNAVAILABLE_PAYMENT_ID))
    );
  },
  { functional: true }
);

export const editEffect = createEffect(
  (action$ = inject(Actions), paymentService = inject(PaymentService), store = inject(Store)) => {
    return action$.pipe(
      ofType(scheduledPaymentsEdit),
      map(action => action.payload),
      switchMap((payload: SchedulePaymentPayloadModel) =>
        paymentService.editScheduledPayment(payload).pipe(
          mergeMap(response => {
            const returnArray: Array<ScheduledPaymentsActions> = [];
            /**
             * Status code of '202304' will be returned if the user is impersonating and is
             * trying to edit a payment without sufficient permission to do so.
             * In this case treat the response code of '202304' as a failure code and stop them from
             * moving forward.
             */
            if (response.status?.messages?.[0]?.code === INVALID_IMPERSONATION_STATUS_CODE) {
              returnArray.push(
                scheduledPaymentsEditFail({
                  request: payload,
                  status: response.status
                })
              );
            } else {
              const conf = paymentService.buildEditScheduledPaymentConfirmationData(
                payload,
                response
              );
              // TODO - this router action should be handled by the component https://amfament.atlassian.net/browse/DRE-34426
              store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.CONFIRMATION_PATH]
                })
              );
              returnArray.push(
                scheduledPaymentsEditSuccess({
                  request: payload,
                  response: response,
                  confirmation: conf
                }),
                scheduledPaymentsCleanup(payload.paymentId),
                scheduledPaymentsLoad()
              );
            }
            return returnArray;
          }),
          catchError(error =>
            of(
              scheduledPaymentsEditFail({
                request: payload,
                status: error?.status as ApiStatus
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

// When editing / deleting a scheduled payment fails we need to cleanup the state (error state is alread in the store)
export const editDeleteFailCleanupEffect = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(scheduledPaymentsEditFail, scheduledPaymentsDeleteFail),
      map(action => scheduledPaymentsCleanup(action.payload.request.paymentId))
    );
  },
  { functional: true }
);

export const deleteEffect = createEffect(
  (action$ = inject(Actions), paymentService = inject(PaymentService), store = inject(Store)) => {
    return action$.pipe(
      ofType(scheduledPaymentsDelete),
      map(action => action.payload),
      switchMap((payload: SchedulePaymentPayloadModel) =>
        paymentService.deleteScheduledPayment(payload).pipe(
          mergeMap(response => {
            const returnArray: Array<ScheduledPaymentsActions> = [];
            /**
             * Status code of '202304' will be returned if the user is impersonating and is
             * trying to edit a payment without sufficient permission to do so.
             * In this case treat the response code of '202304' as a failure code and stop them from
             * moving forward.
             */
            if (response.status?.messages?.[0]?.code === INVALID_IMPERSONATION_STATUS_CODE) {
              returnArray.push(
                scheduledPaymentsDeleteFail({
                  request: payload,
                  status: response.status
                })
              );
            } else {
              const conf = paymentService.buildDeleteScheduledPaymentConfirmationData(
                payload,
                response
              );
              // TODO - this should be handled by the component https://amfament.atlassian.net/browse/DRE-34426
              store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.CONFIRMATION_PATH]
                })
              );
              returnArray.push(
                scheduledPaymentsDeleteSuccess({
                  request: payload,
                  response: response,
                  confirmation: conf
                }),
                scheduledPaymentsTruncate(payload.paymentId)
              );
            }
            return returnArray;
          }),
          catchError(error =>
            of(
              scheduledPaymentsDeleteFail({
                request: payload,
                status: error
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const paymentEditReloadPaymentAccountEffect = createEffect(
  (action$ = inject(Actions), store = inject(Store)) => {
    return action$.pipe(
      ofType(
        ScheduledPaymentsActionTypes.ScheduledPaymentsSubmitSuccessType,
        ScheduledPaymentsActionTypes.ScheduledPaymentsDeleteSuccessType
      ),
      concatLatestFrom(() => [
        store.select(selectPaymentAccountEntities),
        store.select(selectPaymentMethodEntities)
      ]),
      map(([action, , paymentMethodEntities]) => {
        const paymentMethods = paymentMethodEntities;
        const nickName = String(action.payload?.request?.payment?.paymentAccount?.nickName);
        if (nickName && paymentMethods && paymentMethods[nickName]) {
          const paymentAccountId = paymentMethods[nickName].paymentAccountId;
          return PaymentAccountsLoadDetail(paymentAccountId);
        } else {
          // Loading individual payment account didn't work, reload all of them
          return PaymentAccountsLoad();
        }
      })
    );
  },
  { functional: true }
);

// TODO - We should only reload the payment accounts affected (if they switched payment accounts)
export const paymentEditSuccessEffect = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(scheduledPaymentsEditSuccess),
      map(action => action.payload),
      map(() => PaymentAccountsLoad())
    );
  },
  { functional: true }
);

// When payment accounts are edited we need to update the reference in any scheduled payments that use it
// When payment accounts are removed we need to remove any payments that are associated with it
// eslint-disable-next-line @typescript-eslint/naming-convention
export const paymentAccount_edit_delete_successEffect = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(
        PaymentAccountActionTypes.PaymentAccountsEditSuccessType,
        PaymentAccountActionTypes.PaymentAccountsDeleteSuccessType
      ),
      map(() => scheduledPaymentsLoad())
    );
  },
  { functional: true }
);

// When payments are added, edited or deleted we need to query to reload the minimum due
// eslint-disable-next-line @typescript-eslint/naming-convention
export const update_amdEffect = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(
        scheduledPaymentsSubmitSuccess,
        scheduledPaymentsDeleteSuccess,
        scheduledPaymentsEditSuccess
      ),
      map(action => {
        const billAccounts = (action.payload.request?.payment.billAccounts ?? []).map(
          ba => ba.billAccountNumber
        );
        return billAccountReloadMinimumDue(billAccounts);
      })
    );
  },
  { functional: true }
);

// When payment method is edited we need to make sure any payment references are updated
export const editPaymentMethodSuccessEffect = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(editPaymentMethodSuccess),
      map(action => {
        const payload = {
          newNickName: action.newNickName,
          oldNickName: action.oldNickName
        };
        return scheduledPaymentsUpdate(payload);
      })
    );
  },
  { functional: true }
);
